import React, { useMemo, useState } from "react";
import { Row, Col, Card, Segmented } from "antd";
import { Column } from "@ant-design/plots";

const STATUS_ORDER = ["Correct", "Incorrect", "Pending"];
const STATUS_COLORS = {
    Correct: "#87e8de",
    Incorrect: "#ffadd2",
    Pending: "#ffe58f",
};

function normalizeTimeData(rows = []) {
    const rank = (t) => {
        const i = STATUS_ORDER.indexOf(t);
        return i === -1 ? 99 : i;
    };
    return [...rows].sort((a, b) => {
        if (a.key !== b.key) return a.key.localeCompare(b.key);
        return rank(a.type) - rank(b.type);
    });
}

function sumByType(rows) {
    const acc = { Correct: 0, Incorrect: 0, Pending: 0, total: 0 };
    for (const r of rows || []) {
        const c = Number(r?.count || 0);
        acc.total += c;
        if (r?.type && r.type in acc) acc[r.type] += c;
    }
    return acc;
}

const setChartConfig = (rows, setHover) => {
    const data = normalizeTimeData(rows || []);
    return {
        data,
        xField: "label",         
        yField: "count",
        colorField: "type",
        group: { padding: 0 },
        seriesField: "type",
        isGroup: true,

        legend: { position: "bottom" },
        color: (d) => STATUS_COLORS[d.type] ?? "#91caff",

        label: {
            text: (d) => (d.count ?? 0).toLocaleString(),
            position: "top",
            style: { fontSize: 12 },
            content: (d) => (d.count ?? 0).toLocaleString(),
        },

        tooltip: {
            fields: ["label", "type", "count"],
            formatter: (d) => ({ name: d.type, value: (d.count ?? 0).toLocaleString() }),
        },

        style: { radiusTopLeft: 4, radiusTopRight: 4 },
        columnStyle: { radius: [4, 4, 0, 0] },

        axis: {
            x: { labelAutoHide: true, title: null },
            y: { labelFormatter: (v) => Number(v).toLocaleString(), title: null },
        },

        paddingLeft: 8,
        paddingRight: 8,

        onReady: (plot) => {
            plot.on("element:mousemove", (evt) => {
                const d = evt?.data?.data;
                if (d) setHover({ label: d.label, type: d.type, count: d.count });
            });
            plot.on("element:mouseleave", () => setHover(null));
        },
    };
};

const PredictionsByTimeseries = ({ data, isLoading }) => {
    const [period, setPeriod] = useState("lastWeek");
    const [hover, setHover] = useState(null);

    const currentRows = useMemo(() => {
        if (Array.isArray(data)) return data; 
        if (!data) return [];
        return data[period] || [];
    }, [data, period]);

    const totals = useMemo(() => sumByType(currentRows || []), [currentRows]);
    const config = useMemo(() => setChartConfig(currentRows || [], setHover), [currentRows]);

    const HeaderStat = () => {
        if (hover) {
            return (
                <div className="d-none" style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <strong>{hover.label}</strong>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                background: STATUS_COLORS[hover.type] || "#91caff",
                                display: "inline-block",
                                borderRadius: 2,
                            }}
                        />
                        {hover.type}: <b>{hover.count?.toLocaleString?.() ?? hover.count}</b>
                    </span>
                </div>
            );
        }
        return (
            <div className="d-none" style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <span><strong>All</strong>: {totals.total.toLocaleString()}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: STATUS_COLORS.Correct, display: "inline-block", borderRadius: 2 }} />
                    Correct: <b>{totals.Correct.toLocaleString()}</b>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: STATUS_COLORS.Incorrect, display: "inline-block", borderRadius: 2 }} />
                    Incorrect: <b>{totals.Incorrect.toLocaleString()}</b>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: STATUS_COLORS.Pending, display: "inline-block", borderRadius: 2 }} />
                    Pending: <b>{totals.Pending.toLocaleString()}</b>
                </span>
            </div>
        );
    };

    return (
        <Card
            title="Predictions by status (time series)"
            extra={
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <Segmented
                        value={period}
                        onChange={setPeriod}
                        options={[
                            { label: "Last week", value: "lastWeek" },
                            { label: "Last month", value: "lastMonth" },
                            { label: "Last year", value: "lastYear" },
                        ]}
                    />
                    <HeaderStat />
                </div>
            }
        >
            <Row gutter={24}>
                <Col span={24}>
                    <Column {...config} loading={isLoading} className="p-0 m-0" />
                </Col>
            </Row>
        </Card>
    );
};

export default PredictionsByTimeseries;
